// Configure ONE RF sensor — name, type, room, what each code means, which event
// notifies and with what sound, and who is told.
//
// Shared on purpose: it is opened from the RF panel (receiver screen and the
// /rf-devices page) AND from the room, by tapping the sensor's own tile. A safe
// locker should be configurable where the customer sees it, not only where an
// installer first learned it.
//
// `nameOf` is here too: an unnamed transmitter must read as what it is — a
// number nobody has claimed — everywhere it appears.

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Alert, Chip, TextField, MenuItem, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel,
  LinearProgress, Checkbox, IconButton, Tooltip,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SnoozeIcon from '@mui/icons-material/Snooze';
import {
  saveRfDevice, saveRfCode, getRfDeviceAudience, setRfDeviceRecipients, testRfNotify,
  getRooms, getRfDevices, detachRfCode, getAllLocations, getAllRoomsAtLocation,
  RF_CH_PUSH, RF_CH_TELEGRAM, RF_CH_DESKTOP, RF_CH_ALL,
} from '../../core/api/api';
import { rfNumber, rfSensorName } from '../../core/devices/rfIdentify';
import RfUnassignDialog, { canManageRfSensor } from './RfUnassignDialog';
import { invalidateRfDirectory } from '../../core/hooks/useRfDirectory';
import RfScanDialog from './RfScanDialog';
import AutomationsDialog from './AutomationsDialog';
import RfRemindersDialog from './RfRemindersDialog';
import { canRemind } from '../../core/reminders/reminderRules';
import { useApp } from '../../core/state/AppContext';
import { locationLabel } from '../../core/hooks/useLocationChoices';
import {
  RF_TYPES, rfEventChoices, swapPair, canSwapPair, rfTypeLabel, rfSoundFor, rfType,
} from '../../core/devices/rfTypes';
import { binaryOf, eventByteOf } from '../../core/devices/rfCodes';
import { loadSignatures, detectRfType, refreshSignatures } from '../../core/devices/rfSignatures';
import { soundOptions } from '../../core/push/pushChannels';
import { isLifeSafety } from '../../core/security/alertGrid';
import SoundPlayButton from './SoundPlayButton';

export const nameOf = rfSensorName;

// ── Edit one sensor ────────────────────────────────────────────────────────
/**
 * @param device      the RfDevice row (with its Codes), or null for closed
 * @param rooms       optional; fetched from `locationId` when not given, so the
 *                    room screen does not have to hand its list over
 * @param locationId  the location whose rooms to offer
 */
export default function RfSensorDialog({
  device, rooms: roomsProp, locationId = 0, onClose, onSaved,
}) {
  const app = useApp();
  const { userId, isSuperAdmin, locations: myLocations } = app;
  const open = !!device;

  const [form, setForm] = useState(device || {});
  const [codes, setCodes] = useState([]);
  const [audience, setAudience] = useState(null);
  const [recipients, setRecipients] = useState([]);   // [] = nobody is told
  // HOW each ticked person is told: { userId: bits } — 1 phone, 2 Telegram,
  // 4 desktop. Missing = all three. Shown only when the server stores it
  // (audience.channelsSupported — rf_recipient_channels_migration.sql).
  const [channels, setChannels] = useState({});
  const [tested, setTested] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [replacing, setReplacing] = useState(false);
  const [automating, setAutomating] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  // Codes taken off the sensor with 🗑, detached on Save.
  const [detached, setDetached] = useState([]);
  const [allDevices, setAllDevices] = useState([]);
  // What the signature library recognised this sensor as, for the banner. The
  // detection PRE-FILLS the fields below; the installer confirms by saving.
  const [detected, setDetected] = useState(null);

  // ── WHERE the sensor is: a location, and a room in it or none ────────────
  // ⚠️ THE ROOMS ARE THE SENSOR'S LOCATION'S, not the page's. The dialog used to
  // offer only the rooms of whatever location the page was filtered to, and had
  // no location field at all — so a sensor saved under Factory could never be
  // moved to Home from any screen (asked for 2026-10-06).
  //
  // RoomID 0 is a real choice — "whole location", like a common device: a
  // door sensor on the main door belongs to the premises, not to one room.
  //
  // Who may place it where: a Super Admin any location (and every room in it,
  // mapped or not — getAllRoomsAtLocation); everyone else the locations they
  // can reach and the rooms they are mapped to, the scope the RF page uses.
  const [allLocations, setAllLocations] = useState(null);
  useEffect(() => {
    if (!open || !isSuperAdmin || allLocations) return;
    getAllLocations()
      .then((r) => setAllLocations(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAllLocations([]));
  }, [open, isSuperAdmin, allLocations]);
  const locationChoices = isSuperAdmin ? (allLocations || []) : (myLocations || []);

  const sensorLoc = Number(form.LocationID) || 0;
  const [rooms, setRooms] = useState([]);
  useEffect(() => {
    if (!open || !sensorLoc) { setRooms([]); return undefined; }
    // The caller's list is reused only when it is for this very location.
    if (roomsProp && Number(locationId) === sensorLoc) { setRooms(roomsProp); return undefined; }
    let alive = true;
    (isSuperAdmin ? getAllRoomsAtLocation(sensorLoc) : getRooms(userId, sensorLoc))
      .then((r) => { if (alive) setRooms(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setRooms([]); });
    return () => { alive = false; };
  }, [open, sensorLoc, roomsProp, locationId, isSuperAdmin, userId]);

  useEffect(() => {
    setForm(device || {});
    setCodes((device && device.Codes) || []);
    setDetached([]);
    setAudience(null);
    setError('');
    setRecipients([]);
    setChannels({});
    setTested(null);
    setDetected(null);
    if (device?.RfDeviceId) {
      getRfDeviceAudience(device.RfDeviceId)
        .then((r) => {
          setAudience(r.data);
          setRecipients(r.data?.chosen || []);
          setChannels(r.data?.channels || {});
        })
        .catch(() => setAudience(null));
    }
  }, [device]);

  // ── Auto-detect from the signature library ────────────────────────────────
  // Only when something is still UNLABELLED — the type is blank/other, or a code
  // has no meaning. It never overwrites a label already set, so editing a
  // configured sensor is untouched. The company's earlier labels are what make
  // this possible (see core/devices/rfSignatures.js).
  useEffect(() => {
    if (!device) return undefined;
    const list = device.Codes || [];
    const typeBlank = !String(device.RfType || '').trim()
      || String(device.RfType).toLowerCase() === 'other';
    const anyCodeBlank = list.some((c) => !String(c.DeviceAction || '').trim());
    if (!typeBlank && !anyCodeBlank) return undefined;

    const bytes = list
      .map((c) => eventByteOf(Number(c.REMOTEVALUE ?? c.code)))
      .filter((b) => Number.isFinite(b));

    let alive = true;
    loadSignatures().then((sig) => {
      if (!alive) return;
      const det = detectRfType(bytes, sig);
      if (!det) return;
      // Fill only what is blank — never clobber a meaning the installer set.
      if (typeBlank) setForm((f) => ({ ...f, RfType: det.rfType }));
      setCodes((prev) => prev.map((c) => {
        if (String(c.DeviceAction || '').trim()) return c;
        const m = det.meanings.get(eventByteOf(Number(c.REMOTEVALUE ?? c.code)));
        return (m && m.meaning) ? { ...c, DeviceAction: m.meaning } : c;
      }));
      setDetected(det);
    });
    return () => { alive = false; };
  }, [device]);

  if (!device) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const choices = rfEventChoices(form.RfType);
  // One-event types (panic, motion, fire, gas, water) have a single meaning to
  // set; more codes than that on such a sensor are flagged as likely strays.
  const singleEvent = rfType(form.RfType).events.length === 1;
  const extraCodes = singleEvent && codes.length > 1;

  const save = async () => {
    setBusy(true); setError('');
    try {
      await saveRfDevice({
        RfDeviceId: device.RfDeviceId,
        Name: form.Name || '',
        RfType: form.RfType || 'other',
        LocationID: Number(form.LocationID) || 0,
        RoomID: Number(form.LocationID) ? (Number(form.RoomID) || 0) : 0,
        Notify: form.Notify ? 1 : 0,
        NotifySound: form.NotifySound || '',
        NotifyLevel: form.NotifyLevel || 'alert',
        Active: form.Active === undefined ? 1 : (form.Active ? 1 : 0),
      });
      // Each code's meaning, so "Open" and "Close" are saved with the sensor
      // rather than in a second trip the installer has to remember.
      await Promise.all(codes.map((c) => saveRfCode({
        REMOTEVALUE: c.REMOTEVALUE,
        RemoteBinaryValue: c.RemoteBinaryValue || binaryOf(c.REMOTEVALUE),
        UniqueNumber: c.UniqueNumber || device.ReceiverUniqueNumber || '',
        DeviceAction: c.DeviceAction || '',
        DeviceName: form.Name || '',
        // -1 = follow the sensor, 0 = never, 1 = always. Per EVENT, because
        // Open and Close are two different codes.
        Notify: c.Notify === undefined ? -1 : c.Notify,
        NotifySound: c.NotifySound || '',
      })));
      // Codes removed with 🗑 — after the others are saved, so a failure here
      // cannot lose the meanings and sounds that were just set.
      // eslint-disable-next-line no-restricted-syntax
      for (const v of detached) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await detachRfCode(v);
        } catch (e) {
          throw new Error(e?.response?.status === 404
            ? 'The server cannot remove codes yet — deploy the backend (DetachRfCode). The rest was saved.'
            : `Could not remove ${v}. The rest was saved.`);
        }
      }
      await setRfDeviceRecipients(device.RfDeviceId, recipients, audience?.channelsSupported ? channels : null);
      // A fresh label feeds the signature library — so the NEXT sensor of this
      // type is recognised from what was just confirmed here.
      refreshSignatures();
      onSaved();
      onClose();
    } catch (e) {
      // A detach failure carries its own message; anything else is the save itself.
      setError(e && String(e.message).includes('The rest was saved') ? e.message : 'Could not save. Nothing was changed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    // TWO COLUMNS on a wide screen (owner, 2026-10-10: "avoid scrolling"):
    // the sensor on the left, who is told on the right. A phone stacks them.
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      {/* The RF number is the sensor's own, kept for life; hw is the unit
          currently fitted, which Replace hardware changes. */}
      <DialogTitle sx={{ pb: 0.5 }}>
        {nameOf(device)}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
          {[rfNumber(device.RfDeviceId), `hw ${device.TransmitterId}`].filter(Boolean).join(' · ')}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ py: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {detected && (
          <Alert severity="info" icon={<NotificationsActiveIcon fontSize="inherit" />} sx={{ mb: 1 }} onClose={() => setDetected(null)}>
            Auto-detected <b>{rfTypeLabel(detected.rfType)}</b>
            {[...detected.meanings.entries()].some(([, m]) => m.meaning) && (
              <>
                {' — '}
                {[...detected.meanings.entries()]
                  .filter(([, m]) => m.meaning)
                  .map(([byte, m]) => `${byte} = ${m.meaning}`)
                  .join(', ')}
              </>
            )}
            . Pre-filled below — review and <b>Save</b>, or change anything first.
          </Alert>
        )}
        <Box sx={{
          display: 'grid', gap: 2, alignItems: 'start',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.45fr) minmax(0, 1fr)' },
        }}
        >
        {/* ── LEFT: the sensor ─────────────────────────────────────────── */}
        <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
          <TextField
            size="small" label="Name" value={form.Name || ''} onChange={set('Name')}
            placeholder="Safe door, Kitchen fire sensor…" sx={{ flex: '2 1 180px' }}
          />
          <TextField
            select size="small" label="Type" value={form.RfType || 'other'} onChange={set('RfType')}
            sx={{ flex: '1 1 150px' }}
          >
            {RF_TYPES.map((t) => (
              <MenuItem key={t.key} value={t.key}>{`${t.icon}  ${t.label}`}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="Location" value={sensorLoc}
            // A room belongs to ONE location — changing the location clears it.
            onChange={(e) => setForm((f) => ({ ...f, LocationID: Number(e.target.value) || 0, RoomID: 0 }))}
            sx={{ flex: '1 1 160px' }}
          >
            <MenuItem value={0}>No location</MenuItem>
            {/* The current one, even before the list has loaded (or if it is
                not in this user's list), so the select never shows blank. */}
            {sensorLoc > 0 && !locationChoices.some((l) => Number(l.LocationID) === sensorLoc) && (
              <MenuItem value={sensorLoc}>{form.LocationName || `Location ${sensorLoc}`}</MenuItem>
            )}
            {locationChoices.map((l) => (
              <MenuItem key={l.LocationID} value={Number(l.LocationID)}>{locationLabel(l)}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="Room" value={Number(form.RoomID) || 0}
            onChange={(e) => setForm((f) => ({ ...f, RoomID: Number(e.target.value) || 0 }))}
            disabled={!sensorLoc}
            sx={{ flex: '1 1 130px' }}
            helperText={sensorLoc && !(Number(form.RoomID) > 0) ? 'Shown at the top of the location' : undefined}
          >
            <MenuItem value={0}>{sensorLoc ? 'No room — whole location' : 'No room'}</MenuItem>
            {Number(form.RoomID) > 0 && !rooms.some((r) => Number(r.RoomID) === Number(form.RoomID)) && (
              <MenuItem value={Number(form.RoomID)}>{form.RoomName || `Room ${form.RoomID}`}</MenuItem>
            )}
            {rooms.map((r) => <MenuItem key={r.RoomID} value={Number(r.RoomID)}>{r.RoomName}</MenuItem>)}
          </TextField>
        </Box>

        <Divider textAlign="left" sx={{ mt: 1, mb: 0.5, typography: 'caption', color: 'text.secondary' }}>Codes</Divider>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
          {/* The help is the TYPE's: a panic button has one event and nothing to
              pair, so the safe-locker Open/Close advice was noise on it. */}
          <Typography variant="caption" color="text.secondary" sx={{ width: 0, flex: '1 1 0', minWidth: 200, lineHeight: 1.3 }}>
            {singleEvent ? (
              <>
                {`A ${rfType(form.RfType).label.toLowerCase()} sends ONE code — set its sound on that row. `}
                {extraCodes && <b>An extra code is usually a corrupted radio frame — remove it with 🗑.</b>}
              </>
            ) : (
              <>
                Each event is set on its own: a safe locker can ring loudly on <b>Open</b> and
                stay silent on <b>Close</b> — set that event&apos;s Notify to <b>Never</b> to ignore it.
              </>
            )}
          </Typography>
          {/* ⚠️ WHICH CODE IS "OPEN" CANNOT BE DERIVED, ONLY OBSERVED — the event
              byte is the vendor's, not a standard. Getting it backwards is the
              commonest mistake on a magnet sensor, so reversing it is one click
              and takes the sound and the notify setting with it. */}
          {canSwapPair(codes) && (
            <Button
              size="small" startIcon={<SwapVertIcon />}
              onClick={() => setCodes((list) => swapPair(list))}
            >
              Reverse open / close
            </Button>
          )}
        </Box>
        {/* ⚠️ A SENSOR IS NOT ALWAYS A PAIR. Door / safe / shutter send two
            (magnet away = Open, magnet near = Close); fire, motion and panic
            send one. The list is whatever this transmitter has actually sent. */}
        {codes.length === 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            No codes yet. Press the sensor while the Learn list below is open.
          </Alert>
        )}
        {codes.map((c, i) => (
          <Box
            key={c.REMOTEVALUE}
            sx={{
              display: 'flex', gap: 0.75, alignItems: 'center', mb: 0.5, flexWrap: 'wrap',
              p: 0.5, borderRadius: 1, border: 1, borderColor: 'divider',
            }}
          >
            {/* Code + event byte stacked: one narrow column instead of two. */}
            <Box sx={{ minWidth: 84 }}>
              <Typography sx={{ fontFamily: 'monospace', fontSize: 11.5, lineHeight: 1.2 }}>{c.REMOTEVALUE}</Typography>
              <Typography sx={{ fontFamily: 'monospace', fontSize: 10.5, color: 'text.secondary', lineHeight: 1.2 }}>
                {`#${Number(c.REMOTEVALUE) % 256}`}
              </Typography>
            </Box>
            <TextField
              select size="small" label="Means" sx={{ width: 120 }}
              value={choices.includes(c.DeviceAction) ? c.DeviceAction : '__custom__'}
              onChange={(e) => {
                const v = e.target.value;
                setCodes((list) => list.map((x, j) => (j === i
                  ? { ...x, DeviceAction: v === '__custom__' ? '' : v } : x)));
              }}
            >
              {choices.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              <MenuItem value="__custom__">Other…</MenuItem>
            </TextField>
            {!choices.includes(c.DeviceAction) && (
              <TextField
                size="small" label="Meaning" value={c.DeviceAction || ''}
                onChange={(e) => setCodes((list) => list.map((x, j) => (j === i
                  ? { ...x, DeviceAction: e.target.value } : x)))}
                sx={{ width: 120 }}
              />
            )}
            {/* ⚠️ THE EVENT DECIDES. A safe locker shouts on Open and stays
                quiet on Close — one switch for the whole sensor means either a
                silent break-in or a notification every time the door shuts. */}
            <TextField
              select size="small" label="Notify" sx={{ width: 128 }}
              value={c.Notify === undefined || c.Notify === null ? -1 : Number(c.Notify)}
              onChange={(e) => setCodes((list) => list.map((x, j) => (j === i
                ? { ...x, Notify: Number(e.target.value) } : x)))}
            >
              <MenuItem value={-1}>Follow sensor</MenuItem>
              <MenuItem value={1}>Always</MenuItem>
              <MenuItem value={0}>Never</MenuItem>
            </TextField>
            <TextField
              select size="small" label="Sound" sx={{ flex: '1 1 140px', minWidth: 140 }}
              value={c.NotifySound || ''}
              onChange={(e) => setCodes((list) => list.map((x, j) => (j === i
                ? { ...x, NotifySound: e.target.value } : x)))}
            >
              <MenuItem value="">Sensor&apos;s sound</MenuItem>
              {soundOptions(c.NotifySound).map((snd) => <MenuItem key={snd.sound} value={snd.sound}>{snd.name}</MenuItem>)}
            </TextField>
            {/* What this event will ACTUALLY play: its own, else the sensor's,
                else the type's (a door's Close plays "Door closed"). */}
            <SoundPlayButton
              sound={c.NotifySound || form.NotifySound || rfSoundFor({ ...form, NotifySound: '', LastEvent: c.DeviceAction })}
              sx={{ ml: -0.5 }}
            />
            {/* Take a code that is not really this sensor's off it — applied on
                Save, so Cancel leaves everything as it was. */}
            <Tooltip title="Remove this code from the sensor (on Save)">
              <IconButton
                size="small"
                onClick={() => {
                  setDetached((list) => [...list, c.REMOTEVALUE]);
                  setCodes((list) => list.filter((_, j) => j !== i));
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
        {detached.length > 0 && (
          <Alert
            severity="warning" sx={{ mb: 1, py: 0 }}
            action={<Button size="small" color="inherit" onClick={() => { setCodes(device.Codes || []); setDetached([]); }}>Undo</Button>}
          >
            {`${detached.join(', ')} will be removed from this sensor when you Save.`}
          </Alert>
        )}

        <Divider textAlign="left" sx={{ mt: 1, mb: 0.5, typography: 'caption', color: 'text.secondary' }}>Notification</Divider>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControlLabel
            control={(
              <Switch
                checked={!!Number(form.Notify)}
                onChange={(e) => setForm((f) => ({ ...f, Notify: e.target.checked ? 1 : 0 }))}
              />
            )}
            label="Notify on every event"
          />
          <TextField
            select size="small" label="Sensor sound" value={form.NotifySound || ''} onChange={set('NotifySound')}
            sx={{ flex: '1 1 160px', minWidth: 160 }}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">By type (default)</MenuItem>
            {soundOptions(form.NotifySound).map((s) => <MenuItem key={s.sound} value={s.sound}>{s.name}</MenuItem>)}
          </TextField>
          <SoundPlayButton sound={form.NotifySound || rfSoundFor({ ...form, NotifySound: '' })} />
        </Box>
        {!!Number(form.Notify) && !String(form.Name || '').trim() && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            An unnamed sensor never notifies — give it a name first.
          </Alert>
        )}
        </Box>

        {/* ── RIGHT: who is told ───────────────────────────────────────── */}
        <Box sx={{ minWidth: 0 }}>
        <Divider textAlign="left" sx={{ mb: 0.5, typography: 'caption', color: 'text.secondary' }}>Who is told</Divider>
        {/* ⚠️ A CHOSEN LIST BEATS THE COMPUTED ONE, and no choice at all is not
            silence — it falls back to everyone who can see the receiver. The
            tick list is the choice; to tell nobody, set the events to Never. */}
        {audience ? (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, lineHeight: 1.3 }}>
              Anyone given this location can be told — tick and Save.
            </Typography>
            {!audience.candidates?.length && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Nobody has been given this location yet, so there is nobody to tell.
                Add the person to the location first.
              </Alert>
            )}
            <Box sx={{ maxHeight: { xs: 220, md: 300 }, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            {(audience.candidates || []).map((u) => {
              const id = Number(u.UserId);
              const reachable = !!Number(u.Push) || !!Number(u.Telegram);
              return (
                <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 0.5 }}>
                  <Checkbox
                    size="small" sx={{ p: 0.5 }}
                    checked={recipients.includes(id)}
                    onChange={(e) => setRecipients((list) => (e.target.checked
                      ? [...new Set([...list, id])] : list.filter((x) => x !== id)))}
                  />
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                    {u.FullName || `User ${id}`}
                    <Typography component="span" variant="caption" color="text.secondary">
                      {u.MobileNumber ? ` · ${u.MobileNumber}` : ''}
                    </Typography>
                  </Typography>
                  {/* Configured and silent is the failure nobody notices — a
                      recipient with no push token and no linked Telegram is
                      named here rather than counted as a successful send. */}
                  {/* Ticked + the server stores channels: HOW this person is told,
                      three toggles. A channel they cannot receive is shown in
                      orange (no app token / Telegram not linked). */}
                  {audience.channelsSupported && recipients.includes(id) ? (
                    [
                      [RF_CH_PUSH, 'Phone', !!Number(u.Push), 'No app on a phone — a push cannot reach them'],
                      [RF_CH_TELEGRAM, 'Telegram', !!Number(u.Telegram), 'Telegram is not linked for them'],
                      [RF_CH_DESKTOP, 'Desktop', true, ''],
                    ].map(([bit, label, can, why]) => {
                      const bits = Number(channels[id]) || RF_CH_ALL;
                      const on = !!(bits & bit);
                      return (
                        <Chip
                          key={bit}
                          size="small"
                          label={label}
                          variant={on ? 'filled' : 'outlined'}
                          color={on ? (can ? 'primary' : 'warning') : 'default'}
                          title={on && !can ? why : (on ? `Told by ${label.toLowerCase()} — tap to stop` : `Not told by ${label.toLowerCase()} — tap to add`)}
                          // The last channel cannot be switched off: that is "untick", not a channel.
                          onClick={() => setChannels((c) => {
                            const next = bits ^ bit;
                            return next ? { ...c, [id]: next } : c;
                          })}
                          sx={{ ml: 0.5, opacity: on ? 1 : 0.6 }}
                        />
                      );
                    })
                  ) : reachable
                    ? (
                      <>
                        {!!Number(u.Push) && <Chip size="small" label="push" variant="outlined" sx={{ mr: 0.5 }} />}
                        {!!Number(u.Telegram) && <Chip size="small" label="telegram" variant="outlined" />}
                      </>
                    )
                    : <Chip size="small" color="warning" label="unreachable" title="No app and no Telegram — cannot be reached" />}
                </Box>
              );
            })}
            </Box>
            {/* ⚠️ NOBODY TICKED = NOBODY TOLD (owner, 2026-10-10) — the server
                no longer falls back to the receiver's users. Red for a fire /
                panic / gas / water sensor, where silence is dangerous. Intrusion
                alarms still reach everyone with access. */}
            <Alert
              severity={recipients.length ? 'success' : (isLifeSafety(form.RfType) ? 'error' : 'info')}
              sx={{ mt: 0.75, py: 0 }}
            >
              {/* eslint-disable-next-line no-nested-ternary */}
              {recipients.length
                ? (audience.channelsSupported
                  ? `Only these ${recipients.length} will be told — each by the channels lit beside their name.`
                  : `Only these ${recipients.length} will be told — on the app and on Telegram.`)
                : (isLifeSafety(form.RfType)
                  ? `Nobody is ticked — this ${rfType(form.RfType).label.toLowerCase()} will tell NO ONE. Tick at least one person.`
                  : 'Nobody is ticked — nobody will be told. Every event is still recorded; a break-in while locked still alerts everyone.')}
            </Alert>
            {/* Desktop-only people: the desktop rings only where the location is
                OPEN in a browser that has had a click — say so, louder for a
                panic / fire sensor where nobody hearing it is the danger. */}
            {audience.channelsSupported && (() => {
              const deskOnly = recipients.filter((r) => {
                const b = Number(channels[r]) || RF_CH_ALL;
                return !(b & (RF_CH_PUSH | RF_CH_TELEGRAM));
              });
              if (!deskOnly.length) return null;
              const names = deskOnly.map((r) => (audience.candidates || [])
                .find((c) => Number(c.UserId) === r)?.FullName || `User ${r}`).join(', ');
              const allDesk = deskOnly.length === recipients.length;
              return (
                <Alert severity={allDesk && isLifeSafety(form.RfType) ? 'warning' : 'info'} sx={{ mt: 0.75, py: 0 }}>
                  {`Desktop only: ${names}. The desktop rings only while this location is open in a browser `
                    + '(click the page once so it may play sound).'}
                  {allDesk && isLifeSafety(form.RfType) ? ' With no phone or Telegram, a press when no desktop is open reaches no one.' : ''}
                </Alert>
              );
            })()}
          </>
        ) : <LinearProgress sx={{ my: 1 }} />}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Button
            size="small" variant="outlined" startIcon={<NotificationsActiveIcon />}
            disabled={busy || !device.RfDeviceId}
            onClick={async () => {
              setTested(null);
              const r = await testRfNotify(device.RfDeviceId, 'Test').catch(() => null);
              setTested(r?.data || { ok: false, error: 'the test could not be sent' });
            }}
          >
            Send a test
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ width: 0, flex: '1 1 0', minWidth: 140, lineHeight: 1.3 }}>
            Proves the whole chain. Not logged as an event.
          </Typography>
        </Box>
        {/* ⚠️ PER CHANNEL, NOT A TOTAL. "Sent to 2" hides two Telegram messages
            and no push at all — which is the case an installer is testing for. */}
        {tested && (
          <Alert severity={tested.ok && tested.sent ? 'success' : 'warning'} sx={{ mt: 1 }}>
            {tested.ok ? (
              <>
                <Typography variant="body2">
                  {`📱 FCM push: ${tested.push?.sent ?? 0} sent`}
                  {tested.push?.noToken ? ` · ${tested.push.noToken} with no app installed` : ''}
                  {tested.push?.failed ? ` · ${tested.push.failed} failed` : ''}
                </Typography>
                <Typography variant="body2">
                  {`💬 Telegram: ${tested.telegram?.sent ?? 0} sent`}
                  {tested.telegram?.notLinked ? ` · ${tested.telegram.notLinked} not linked` : ''}
                  {tested.telegram?.failed ? ` · ${tested.telegram.failed} failed` : ''}
                </Typography>
                {!tested.sent && (
                  <Typography variant="caption">
                    Nothing arrived anywhere — link Telegram, or open the app once on the phone
                    so it registers for push.
                  </Typography>
                )}
              </>
            ) : (tested.error || 'nobody would be told')}
          </Alert>
        )}
        </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        {/* A broken or swapped unit: scan the new one onto THIS sensor, so it
            keeps its RF number, name, room, who is told, history and actions. */}
        {!!device.RfDeviceId && (
          <Button
            color="warning" startIcon={<SwapHorizIcon />} disabled={busy}
            onClick={async () => {
              const r = await getRfDevices({}).catch(() => null);
              setAllDevices(Array.isArray(r?.data) ? r.data : []);
              setReplacing(true);
            }}
          >
            Replace hardware…
          </Button>
        )}
        {/* What this sensor switches, and when — runs on the server. Needs the
            sensor saved at a location first (the rules live per location). */}
        {!!device.RfDeviceId && (
          <Button size="small" disabled={busy || !Number(device.LocationID)} onClick={() => setAutomating(true)}
            title={Number(device.LocationID) ? '' : 'Save the sensor at a location first'}>
            Automations…
          </Button>
        )}
        {/* Nag ONE named person while the sensor stays open — the phone's way
            in, since a tile there has no right-click menu. Door / safe /
            shutter only: a reminder needs a state that can end. */}
        {!!device.RfDeviceId && canRemind(device) && (
          <Button size="small" startIcon={<SnoozeIcon />} disabled={busy} onClick={() => setReminding(true)}>
            Reminders…
          </Button>
        )}
        {/* Back to a fresh sensor for EVERYONE — the same confirm (with the
            counts, before the write) as the tile's right-click Unassign…. */}
        {!!device.RfDeviceId && canManageRfSensor(device, app) && (
          <Button color="error" size="small" disabled={busy} onClick={() => setUnassigning(true)}>
            Unassign…
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy}>Save</Button>
      </DialogActions>
      <RfRemindersDialog
        open={reminding}
        onClose={() => setReminding(false)}
        device={device}
      />
      <AutomationsDialog
        open={automating}
        onClose={() => setAutomating(false)}
        locationId={Number(device.LocationID) || 0}
        locationName={device.LocationName || ''}
        forSensor={{ rfDeviceId: device.RfDeviceId }}
      />
      <RfUnassignDialog
        device={unassigning ? device : null}
        onClose={() => setUnassigning(false)}
        onDone={() => {
          setUnassigning(false);
          onSaved();
          onClose();
        }}
      />
      <RfScanDialog
        open={replacing}
        replace={device}
        receiverUno={device.ReceiverUniqueNumber || ''}
        devices={allDevices}
        onClose={() => setReplacing(false)}
        onSaved={() => {
          invalidateRfDirectory();
          setReplacing(false);
          onSaved();
          onClose();
        }}
      />
    </Dialog>
  );
}
